import { FC, useContext, useEffect, useState, useMemo, useRef, JSX } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { toast } from "react-toastify";
import { orderBy } from "lodash";
import { ColumnConfig, SortDirections, TextAlignment, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { UserContext } from "@app/user/UserContext";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import RoleRestricted from "@app/user/RoleRestricted";
import RoleModel from "@app/user/roleModel";
import RoleService from "@app/user/roleService";
import Roles from "@app/user/roles";
import { ClientListItem } from "@app/client/client";
import RoleGroupService from "@app/admin/role-groups/roleGroupService";
import { RoleGroupListModel, RoleGroupModel, AddRoleGroupModel, UpdateRoleGroupModel } from "@app/admin/role-groups/roleGroup";
import CreateEditPanelContent from "@app/admin/role-groups/CreateEditPanelContent";


interface RoleGroupTableData {
  [wafflesKeyColumnName]: string;
  name: JSX.Element;
  clientName: string;
  createdBy: string;
  roles: string;
  isActive: JSX.Element;
  deleteBtn: JSX.Element;
}

const clientRoleGroupColumnConfig: ColumnConfig = {
  clientName: {
    displayText: "Client Name",
    sizeContentToFit: true,
    testId: "clientName",
  },
  createdBy: {
    displayText: "Created By",
    sizeContentToFit: true,
    testId: "createdBy",
  },
  deleteBtn: {
    alignment: TextAlignment.Right,
    displayText: "",
    noResize: true,
    noSort: true,
    testId: "delete",
  },
  isActive: {
    displayText: "Is Active",
    sizeContentToFit: true,
    testId: "isActive",
  },
  name: {
    displayText: "Name",
    sizeContentToFit: true,
    testId: "name",
  },
  roles: {
    displayText: "Roles",
    noSort: true,
    testId: "roles",
    wrapContent: true
  }
};

interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<RoleGroupTableData>,
}

interface RoleGroupsMatchProps {
  clientId?: string;
}

type RoleGroupsProps = RouteComponentProps<RoleGroupsMatchProps, undefined, { clientName: string }>;

export const RoleGroups: FC<React.PropsWithChildren<RoleGroupsProps>> = (props: RoleGroupsProps) => {

  const roleGroupService = new RoleGroupService();

  const user = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [sidePanelLoading, setSidePanelLoading] = useState(false);

  const [isQuestAdmin] = useState(user.roles.some(x => x === Roles.QUEST_ADMIN));
  const [clientRoleGroups, setClientRoleGroups] = useState<RoleGroupTableData[]>([]);

  const [take] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [numRecordsDisplayed, setNumRecordsDisplayed] = useState(0);
  const [roleGroupNameFilter, setRoleGroupNameFilter] = useState("");
  const [clientNameFilter, setClientNameFilter] = useState("");
  const [isFiltered, setIsFiltered] = useState(false);
  const [pageSettings, setPageSettings] = useState<PageSettings>(
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      },
    });
  const [allRoles, setAllRoles] = useState<RoleModel[]>([]);
  const [roleFilter, setRoleFilter] = useState<DropdownItem<number>[]>([]);

  const firstRun = useRef(true);

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take }),
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: totalRecords,
  }

  const breadcrumbs: (string | Breadcrumb)[] = useMemo(() => {
    const breadcrumbs: (string | Breadcrumb)[] = [];

    if (!!props.match.params.clientId) {
      breadcrumbs.push(
        {
          link: `/admin/client-administration/${props.match.params.clientId}`,
          text: !!props.location.state ? props.location.state.clientName : "Client",
        });
    }

    breadcrumbs.push("Role Groups");

    return breadcrumbs;
  }, [props.match.params.clientId, props.location.state]);

  useEffect(() => {
    fetchAllRoles();
  }, []);

  useEffect(() => {
    if (firstRun.current && allRoles.length > 0)
      fetchRoleGroups();
  }, [roleFilter]);

  useEffect(() => {
    if (firstRun.current || allRoles.length === 0)
      return;

    fetchRoleGroups();
  }, [pageSettings]);

  useEffect(() => {
    setRoleFilter(getAllRoleDropdownItems());
  }, [allRoles]);

  const fetchRoleGroups = async () => {
    setLoading(true);

    let selectedRoles = roleFilter;
    if (selectedRoles.length === 0) {
      selectedRoles = getAllRoleDropdownItems();
      setRoleFilter(selectedRoles);
    }
    const selectedRoleIds = selectedRoles.map(x => x.key);

    let roleGroupList: RoleGroupListModel = { roleGroupList: [], totalCount: 0 };
    if (!!props.match.params.clientId || !isQuestAdmin) {
      const clientId = !!props.match.params.clientId ? parseInt(props.match.params.clientId) : user.clientId;
      roleGroupList = await roleGroupService.getRoleGroupsByClientId(clientId, false, true, true, true, pageSettings.skip, take, pageSettings.sortDetails.sortDirection, pageSettings.sortDetails.sortedColumn, roleGroupNameFilter, clientNameFilter, selectedRoleIds);
    } else {
      roleGroupList = await roleGroupService.getAllRoleGroups(false, true, true, true, pageSettings.skip, take, pageSettings.sortDetails.sortDirection, pageSettings.sortDetails.sortedColumn, roleGroupNameFilter, clientNameFilter, selectedRoleIds);
    }
    setClientRoleGroups(createWafflesTable(roleGroupList.roleGroupList));

    setIsFiltered(!!(roleGroupNameFilter || clientNameFilter || roleFilter.length !== allRoles.length));
    setTotalRecords(roleGroupList.totalCount);
    setNumRecordsDisplayed(roleGroupList.roleGroupList.length);

    firstRun.current = false;
    setLoading(false);
  }

  const createWafflesTable = (data: RoleGroupModel[]): RoleGroupTableData[] => {
    return data.map((rec) => {
      /* oxlint-disable sort-keys */
      let name: JSX.Element = <>{rec.name}</>;
      const canEdit = rec.editUsers.some(x => x.userId === user.userId);
      if (canEdit)
        name =
          <FlyoutLink
            content={{
              body: <CreateEditPanelContent
                currentUserid={user.userId}
                defaultClient={{ clientId: rec.clientId, name: rec.clientName } as ClientListItem}
                allowClientEdit={false}
                handleSave={handleRoleGroupUpdate}
                loading={sidePanelLoading}
                roleGroup={rec} />,
              title: "Edit an Existing Role Group",
            }}
            testId="role-groups__edit-group-flyout"
            classNames="text-info"
            size="40"
          >
            {rec.name}
          </FlyoutLink>;

      let deleteBtn: JSX.Element = <></>;
      if (canEdit)
        deleteBtn =
          <ModalLink
            content={{
              body: (<>
                <p>
                  This will delete the following role group. Are you sure?
                  {rec.users?.length > 0 &&
                    <>
                      <br />
                      <i className="fa fa-exclamation-triangle" style={{ color: "#ffd654" }} /* color taken from $qes-tertiary-yellow-2 */ />
                      <span className="demibold ms-1 d-inline-block">One or more users are assigned to this role group.</span>
                    </>
                  }
                </p>
                <span className="ms-3">{rec.name}</span>

              </>),
              title: "Delete Role Group",
              cancelButtonText: "Cancel",
              confirmButtonAction: () => handleRoleGroupDelete(rec.roleGroupId),
              confirmButtonText: "Delete",
              confirmButtonType: "primary-danger",
            }}
            testId="role-groups__delete-group-modal"
            classNames="qes-button-secondary danger"
            hideBodyScroll={true}
          >
            Delete
          </ModalLink>

      return ({
        key: rec.roleGroupId.toString(),
        name: name,
        clientName: rec.clientName,
        createdBy: rec.createdBy.userName,
        roles: orderBy(rec.roles, (r) => r.name, "asc").map(r => r.name).join(", "),
        isActive: (
          <QESCheckbox
            label=""
            testId="role-groups__is-active"
            checked={rec.isActive}
            disabled={!canEdit}
            onChange={(e) => handleActivateRoleGroup(rec.roleGroupId, e.target.checked)}
          />
        ),
        deleteBtn: deleteBtn

      });
      /* oxlint-enable sort-keys */
    });
  }

  const handleSort = (data: RoleGroupTableData[], newSortedColumn: keyof RoleGroupTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: direction,
        sortedColumn: newSortedColumn,
      }
    });
    return data;
  }

  const handleSearch = () => {
    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const clearFilter = () => {
    setRoleGroupNameFilter("");
    setClientNameFilter("");
    setRoleFilter(getAllRoleDropdownItems());
    setIsFiltered(false);

    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleRoleGroupCreate = async (onClose: () => void, name: string, roleIds: number[], isActive: boolean, clientId: number, editUsers: number[], _roleGroupId?: number, assignToUserIds?: number[]) => {
    const createModel: AddRoleGroupModel = {
      clientId,
      editUserIds: editUsers,
      isActive,
      name,
      roleIds,
      userIds: assignToUserIds || []
    }
    
    setSidePanelLoading(true);
    roleGroupService.addRoleGroup(createModel)
      .then(() => {
        onClose();
        setSidePanelLoading(false);
        fetchRoleGroups();
      }, () => {
        toast("An error occurred while creating this role group.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        onClose();
        setSidePanelLoading(false);
      }
      );
  }

  const handleRoleGroupUpdate = async (onClose: () => void, name: string, roleIds: number[], isActive: boolean, clientId: number, editUsers: number[], roleGroupId?: number, assignToUserIds?: number[]) => { 
    const createModel: UpdateRoleGroupModel = {
      clientId,
      editUserIds: editUsers,
      isActive,
      name,
      roleIds,
      userIds: assignToUserIds || [],
    }

    setSidePanelLoading(true);
    roleGroupService.updateRoleGroup(roleGroupId, createModel)
      .then(() => {
        onClose();
        setSidePanelLoading(false);
        fetchRoleGroups();
      }, () => {
        toast("An error occurred while updating this role group.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        onClose();
        setSidePanelLoading(false);
      }
      );
  }

  const handleActivateRoleGroup = async (roleGroupId: number, isActive: boolean) => {
    setLoading(true);
    roleGroupService.activateRoleGroup(roleGroupId, isActive)
      .then(() => {
        setLoading(false);
        fetchRoleGroups();
      }, () => {
        toast("An error occurred while activating this role group.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        setLoading(false);
        fetchRoleGroups();
      }
      );
  }

  const handleRoleGroupDelete = async (roleGroupId: number) => {
    setLoading(true);
    roleGroupService.deleteRoleGroupById(roleGroupId)
      .then(() => {
        setLoading(false);
        fetchRoleGroups();
      }, () => {
        toast("An error occurred while deleting this role group.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        setLoading(false);
        fetchRoleGroups();
      }
      );
  }

  const fetchAllRoles = async () => {
    setLoading(true);
    const roles = await new RoleService().getAllRoles();
    setAllRoles(orderBy(roles, (r) => r.name, "asc"));
    setLoading(false);
  }

  const getAllRoleDropdownItems = () => {
    return allRoles.map(x => {
      return { key: x.roleId, name: x.name }
    });
  }

  return (
    <>
      <Page documentTitle="Role Groups - Admin">
        <Breadcrumbs
          template={!!props.match.params.clientId ? BreadcrumbTemplateEnum.ClientAdministration : BreadcrumbTemplateEnum.Admin}
          items={breadcrumbs}
        />
        <PageInner>
          <PageHeader title="Role Groups">
            <PageHeaderRight>
              <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN]}>
                <Link
                  className="qes-button qes-button-secondary"
                  to="/admin/internal-reports/role-assignments"
                >
                  Role Assignments
                </Link>
              </RoleRestricted>
              <FlyoutLink
                content={{
                  body: <CreateEditPanelContent
                    currentUserid={user.userId}
                    defaultClient={{ clientId: user.clientId, name: user.clientName } as ClientListItem}
                    allowClientEdit={isQuestAdmin && !props.match.params.clientId}
                    handleSave={handleRoleGroupCreate}
                    loading={sidePanelLoading} />,
                  title: "Add a New Role Group",
                }}
                testId="role-groups__create-group-flyout"
                classNames="qes-button-primary"
                size="40"
              >
                Add Role Group
              </FlyoutLink>
            </PageHeaderRight>
          </PageHeader>
          <PageSection>
            <div className="role-groups-filters mb-3 mt-5">
              <QESTextInput containerClassName="role-groups-filters__input" label="Role Group Name" testId="role-groups-filter__role-group-name" value={roleGroupNameFilter} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setRoleGroupNameFilter(e.target.value)} />
              <QESTextInput
                containerClassName="role-groups-filters__input"
                label="Client Name"
                testId="role-groups-filter__client-name"
                value={clientNameFilter}
                onKeyUp={(e) => handleSearchKeyUp(e)}
                onChange={(e) => setClientNameFilter(e.target.value)} />
              <MultiselectDropdown
                containerClassName="role-groups-filters__input"
                label="Selected Roles"
                disabled={false}
                items={getAllRoleDropdownItems()}
                onSelectionChanged={(selectedItems: DropdownItem<number>[]) => { setRoleFilter(selectedItems); }}
                selectedItems={roleFilter}
                testId="role-groups__roles-filter"
              />
              <QESButton qesType="primary" qesTestId="role-groups__search-button" onClick={handleSearch}>Search</QESButton>
              <QESButton qesType="secondary" qesTestId="role-groups__clear-filters" onClick={clearFilter} disabled={!isFiltered}>Clear</QESButton>
            </div>
            <p>Displaying {numRecordsDisplayed} of {totalRecords} role groups {isFiltered ? "(filtered)" : ""}</p>
            <div className="row mt-3">
              <div className="col-12">
                <Waffles<RoleGroupTableData>
                  columns={clientRoleGroupColumnConfig}
                  customSort={handleSort}
                  data={clientRoleGroups}
                  enablePagination={true}
                  fullWidth={true}
                  loading={loading}
                  passThroughSorting={true}
                  paginationProps={paginationProps}
                  testId="client-role-groups-waffles-table"
                />
              </div>
            </div>
          </PageSection>
        </PageInner>
      </Page>
    </>
  );
}